import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { FaCalendarAlt } from "react-icons/fa";

const OnboardingStep1 = ({ activeItems, toggleSubMenu, handleNext }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [preOpDate, handlePreOpDateChange] = useState(null);
  const [postOpDate, handlePostOpDateChange] = useState(null);

  const isActive = (item) => activeItems.includes(item);

  const handleToggle = (group, item) => {
    // Deactivate all items in the group before activating the new item
    const groupItems =
      group === "step1-1"
        ? ["pre-op", "post-op", "preventive"]
        : ["less-than-a-week", "a-few-weeks", "a-few-months", "over-a-year"];
    groupItems.forEach((activeItem) => {
      if (isActive(activeItem)) {
        toggleSubMenu(activeItem);
      }
    });
    toggleSubMenu(item);
  };

  const handleNextWithFadeOut = () => {
    const isPreventive = isActive("preventive");
    const hasPreOpDate = preOpDate !== null;
    const hasPostOpDate = postOpDate !== null;

    if (isPreventive) {
      localStorage.setItem("is_preventive", true);
      localStorage.setItem("day_zero", null);

      if (isActive("less-than-a-week")) {
        localStorage.setItem("onset_of_pain", "less than a week");
      } else if (isActive("a-few-weeks")) {
        localStorage.setItem("onset_of_pain", "a few weeks");
      } else if (isActive("a-few-months")) {
        localStorage.setItem("onset_of_pain", "a few months");
      } else if (isActive("over-a-year")) {
        localStorage.setItem("onset_of_pain", "over a year");
      }
    }
    if (hasPreOpDate || hasPostOpDate) {
      const formattedDate =
        preOpDate || postOpDate
          ? (preOpDate || postOpDate).toISOString().split("T")[0]
          : null;

      localStorage.setItem("is_preventive", false);
      localStorage.setItem("day_zero", formattedDate);
      localStorage.removeItem("onset_of_pain");
    }

    setFadeOut(true);
    setTimeout(() => {
      handleNext();
    }, 500); // Duration of the fade-out animation
  };

  return (
    <div
      className={`modal-container animated-onboarding onboarding-step1 ${
        fadeOut ? "fade-out-left" : ""
      }`}
    >
      <div className="modal-container-title heading fade1 typewriter-container">
        <span className="typewriter">Recovery starts with your story.</span>
      </div>
      <div className="modal-contect-title fade1-2 subheading">
        Let’s learn a bit more about you!
      </div>
      <div className="modal-contect initial-q">
        <ul className="selection">
          <li
            key="step1-1-pre-op"
            className={`pre-op fade1-3 ${isActive("pre-op") ? "active" : ""}`}
            onClick={() => handleToggle("step1-1", "pre-op")}
          >
            🏥 I’m getting a total knee replacement
          </li>
          <ul
            className={`submenu pre-op-submenu ${
              isActive("pre-op") ? "fadeInDown" : "hidden"
            }`}
          >
            <li key="pre-op-date">
              When is it scheduled?{" "}
              <div className="datepicker-container">
                <div className="custom-datepicker-wrapper">
                  <FaCalendarAlt className="calendar-icon" />
                  <DatePicker
                    selected={
                      preOpDate ||
                      new Date(new Date().setDate(new Date().getDate() + 1))
                    }
                    onChange={handlePreOpDateChange}
                    placeholderText="Select a date"
                    dateFormat="MMMM d, yyyy"
                    className="custom-datepicker"
                    minDate={
                      new Date(new Date().setDate(new Date().getDate() + 1))
                    }
                    maxDate={
                      new Date(new Date().setDate(new Date().getDate() + 140))
                    }
                    popperProps={{
                      positionFixed: true,
                      modifiers: {
                        preventOverflow: {
                          enabled: true,
                          boundariesElement: "viewport",
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </li>
          </ul>
          <li
            key="step1-1-post-op"
            className={`post-op fade1-3 ${isActive("post-op") ? "active" : ""}`}
            onClick={() => handleToggle("step1-1", "post-op")}
          >
            🦵 I just had a total knee replacement
          </li>
          <ul
            className={`submenu selection post-op-submenu ${
              isActive("post-op") ? "fadeInDown" : "hidden"
            }`}
          >
            <li key="post-op-date">
              When was your surgery?{" "}
              <div className="datepicker-container">
                <div className="custom-datepicker-wrapper">
                  <FaCalendarAlt className="calendar-icon" />
                  <DatePicker
                    selected={
                      postOpDate ||
                      new Date(new Date().setDate(new Date().getDate() - 1))
                    } // Set initial date to today if selectedDate is null
                    onChange={handlePostOpDateChange}
                    placeholderText="Select a date"
                    dateFormat="MMMM d, yyyy"
                    className="custom-datepicker"
                    maxDate={
                      new Date(new Date().setDate(new Date().getDate() - 1))
                    }
                    minDate={
                      new Date(new Date().setDate(new Date().getDate() - 224))
                    } // Allow selection up to 32 weeks (224 days) from today
                  />
                </div>
              </div>
            </li>
          </ul>
          <li
            key="step1-1-preventive"
            className={`preventive fade1-3 ${
              isActive("preventive") ? "active" : ""
            }`}
            onClick={() => handleToggle("step1-1", "preventive")}
          >
            🤕 No surgery here, but my knee has been bothering me
          </li>
          <ul
            className={`submenu selection preventive-submenu ${
              isActive("preventive") ? "fadeInDown" : "hidden"
            }`}
          >
            <li key="preventive-duration">
              How long has it been bothering you?
              <div>
                <ul className="selection selection-flex step1-3">
                  <li
                    key="step1-2-less-than-a-week"
                    className={`${
                      isActive("less-than-a-week") ? "active" : ""
                    }`}
                    onClick={() => handleToggle("step1-2", "less-than-a-week")}
                  >
                    Less than a week
                  </li>
                  <li
                    key="step1-2-a-few-weeks"
                    className={`${isActive("a-few-weeks") ? "active" : ""}`}
                    onClick={() => handleToggle("step1-2", "a-few-weeks")}
                  >
                    A few weeks
                  </li>
                  <li
                    key="step1-2-a-few-months"
                    className={`${isActive("a-few-months") ? "active" : ""}`}
                    onClick={() => handleToggle("step1-2", "a-few-months")}
                  >
                    A few months
                  </li>
                  <li
                    key="step1-2-over-a-year"
                    className={`${isActive("over-a-year") ? "active" : ""}`}
                    onClick={() => handleToggle("step1-2", "over-a-year")}
                  >
                    Over a year
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </ul>
      </div>
      {activeItems.length > 0 && (
        <>
          <div className="nav-container fade-fast">
            <button
              className="btn-next next-btn"
              onClick={handleNextWithFadeOut}
            >
              Next
            </button>
          </div>
          <div className="dot-container fade-fast">
            <div className="dot-checked"></div>
            <div className="dot-unchecked"></div>
            <div className="dot-unchecked"></div>
            <div className="dot-unchecked"></div>
            <div className="dot-unchecked"></div>
            <div className="dot-unchecked"></div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnboardingStep1;
